import { FC } from 'react'
import { Auth } from '@aws-amplify/auth'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { PageType } from 'featureSliced/padges/MainScreen'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { RenderIf } from 'components/RenderIf'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { IconStroked } from 'components/IconsContainers/styled'

import { ReactComponent as EditIcon } from 'assets/profile/edit.svg'
import { ReactComponent as CardIcon } from 'assets/profile/card.svg'
import { ReactComponent as PaymentVariant } from 'assets/profile/credit_card.svg'
import { ReactComponent as OrdersIcon } from 'assets/profile/orders.svg'
import { ReactComponent as WishListIcon } from 'assets/profile/wishlist.svg'
import { ReactComponent as ExitIcon } from 'assets/profile/exit.svg'

import { Page, ProfileOption } from './styled'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AuthSlice } from 'featureSliced/shared/store/reducers/AuthReducer'

type MenuProps = {
  setCurrentPage: (page: PageType) => void
  onNext: () => void
  closeThisModal: () => void
}

export const Menu: FC<MenuProps> = ({
  setCurrentPage,
  onNext,
  closeThisModal,
}) => {
  // Hooks
  const t = useTranslations()
  const navigate = useNavigate()
  const dispatch = useMyDispatch()
  const [searchParams] = useSearchParams()
  const table = searchParams.get('table')
  const { registerId, dataEnc } = handleLocalStorage(['registerId', 'dataEnc'])
  const isDatatransActivated = useMySelector((state) => {
    return state.app.companyData.DatatransActivated
  })
  const paymentProvider = useMySelector(
    (state) => state.app.paymentConfig?.paymentProviderType
  )
  const signOut = AuthSlice.actions.signOut

  // Functions
  const handleWishlistClick = () => {
    if (table) {
      navigate(`/wishlist?table=${table}`)
    }
    if (!table) {
      navigate('/wishlist')
    }
    closeThisModal()
  }

  const logOut = async () => {
    if (navigator.onLine) {
      await Auth.signOut()
      dispatch(signOut())
      window.localStorage.removeItem('userName')
      closeThisModal()
    }
  }

  return (
    <>
      <Page>{t('profile.myProfile')}</Page>

      <RenderIf condition={navigator.onLine}>
        <ProfileOption
          onClick={() => {
            setCurrentPage('edit')
            onNext()
          }}
        >
          <IconStroked>
            <EditIcon />
          </IconStroked>
          <p>{t('profile.editProfile')}</p>
        </ProfileOption>
      </RenderIf>

      <ProfileOption
        onClick={() => {
          setCurrentPage('orders')
          onNext()
        }}
      >
        <IconStroked>
          <OrdersIcon />
        </IconStroked>
        <p>{t('profile.orders')}</p>
      </ProfileOption>

      <ProfileOption
        onClick={() => {
          setCurrentPage('card')
          onNext()
        }}
      >
        <IconStroked>
          <CardIcon />
        </IconStroked>
        <p>{t('profile.card')}</p>
      </ProfileOption>

      <RenderIf condition={isDatatransActivated || paymentProvider === 3}>
        <ProfileOption
          onClick={() => {
            setCurrentPage('payment')
            onNext()
          }}
        >
          <IconStroked>
            <PaymentVariant />
          </IconStroked>
          <p>{t('profile.paymentMethod')}</p>
        </ProfileOption>
      </RenderIf>

      <RenderIf condition={registerId || dataEnc}>
        <ProfileOption onClick={handleWishlistClick}>
          <IconStroked>
            <WishListIcon />
          </IconStroked>
          <p>{t('profile.wishlist')}</p>
        </ProfileOption>
      </RenderIf>

      <RenderIf condition={navigator.onLine}>
        <ProfileOption onClick={logOut}>
          <IconStroked>
            <ExitIcon />
          </IconStroked>
          <p>{t('profile.logout')}</p>
        </ProfileOption>
      </RenderIf>
    </>
  )
}
